import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Page } from 'src/app/shared/models/paging/page';
import { loadProposalHistoryGridComponent } from 'src/app/shared/components/proposal-history-grid/lazy-load-proposal-history-grid';
import { ProposalHistoryGridDataSource, ProposalHistoryStageFilterOption } from './contracts/proposal-history-grid-data-source.contract';
import { RefCountedSharedStoreBase } from '../common/ref-counted-shared-store.base';
import { ProposalHistoryRepositoryAdapter } from './repository/proposal-history-repository.adapter';
import { mapProposalHistoryParallelLoad } from './mappers/proposal-history.mapper';
import {
  CrmProposalHistoryWidgetData,
  createEmptyCrmProposalHistoryWidgetData,
} from './models/crm-proposal-history-widget.state';
import { InquiryStageStat } from './models/inquiry-stage-stat.model';
import { ReturnResult } from 'src/app/shared/models/return-result';
import { AutomateDataState } from 'src/app/modules/admin/profile-management/profile-detail/automate-datastate-log/automate-datastate-model';

/**
 * @summary Feature store for proposal-history summary and grid list.
 * @description
 * Central state coordinator used by proposal-history widget + modal grid.
 * It keeps UI consumers decoupled from API orchestration and exposes a stable
 * contract via {@link ProposalHistoryGridDataSource}.
 *
 * Business behavior:
 * - summary counters and grid list are loaded from the same query identity
 * - stale responses are ignored when users switch query/filter quickly
 * - state is auto-reset when last UI consumer detaches
 *
 * @implements @see {@link ProposalHistoryGridDataSource}
 * @references
 * @see {@link ProposalHistoryGridDataSource}
 * @see {@link RefCountedSharedStoreBase}
 * @see {@link ProposalHistoryRepositoryAdapter}
 * @see {@link mapProposalHistoryParallelLoad}
 */
@Injectable()
export class CrmProposalHistoryWidgetStore extends RefCountedSharedStoreBase implements ProposalHistoryGridDataSource {
  private readonly _data$ = new BehaviorSubject<CrmProposalHistoryWidgetData>(createEmptyCrmProposalHistoryWidgetData());
  readonly pageSize = 25;

  /** Full state snapshot (imperative reads, e.g. `this.store.data.summaryLoading`). */
  get data(): CrmProposalHistoryWidgetData {
    return this._data$.value;
  }

  /** Emits the full state object whenever any field changes. */
  readonly data$ = this._data$.asObservable();

  readonly summaryLoading$ = this._data$.pipe(
    map(d => d.summaryLoading),
    distinctUntilChanged(),
  );
  readonly total$ = this._data$.pipe(
    map(d => d.total),
    distinctUntilChanged(),
  );
  readonly listLoading$ = this._data$.pipe(
    map(d => d.listLoading),
    distinctUntilChanged(),
  );
  readonly loadAll$ = this._data$.pipe(
    map(d => d.loadAll),
    distinctUntilChanged(),
  );
  readonly gridMounting$ = this._data$.pipe(
    map(d => d.gridMounting),
    distinctUntilChanged(),
  );
  readonly gridMounted$ = this._data$.pipe(
    map(d => d.gridMounted),
    distinctUntilChanged(),
  );
  readonly gridLoadError$ = this._data$.pipe(
    map(d => d.gridLoadError),
    distinctUntilChanged(),
  );
  readonly summarySkeletonVisible$ = this._data$.pipe(
    map(d => d.summaryLoading && d.total <= 0 && (!d.stageStats || d.stageStats.length === 0)),
    distinctUntilChanged(),
  );
  readonly modalBodyLoading$ = this._data$.pipe(
    map(d => d.gridMounting || d.listLoading),
    distinctUntilChanged(),
  );
  readonly modalBodyError$ = this._data$.pipe(
    map(d => d.gridLoadError && !d.gridMounting && !d.listLoading),
    distinctUntilChanged(),
  );
  readonly stageList$ = this._data$.pipe(
    map(d => d.stageList),
    distinctUntilChanged(),
  );
  readonly stageStats$ = this._data$.pipe(
    map(d => d.stageStats),
    distinctUntilChanged(),
  );
  readonly stageFilterOptions$ = this._data$.pipe(
    map(d => this.buildStageFilterOptions(d.stageList || [], d.stageStats || [], d.pageData?.data || [])),
    distinctUntilChanged((prev, curr) =>
      prev.length === curr.length
      && prev.every((option, index) =>
        option.value === curr[index]?.value && option.text === curr[index]?.text),
    ),
  );
  readonly pageData$ = this._data$.pipe(
    map(d => d.pageData),
    distinctUntilChanged(),
  );

  private requestSeq = 0;
  private loadInFlight: Promise<void> | null = null;

  constructor(private proposalHistoryRepository: ProposalHistoryRepositoryAdapter) {
    super();
  }

  /** Auto-reset state when the last attached UI consumer is disposed. */
  protected onLastConsumerDetached(): void {
    this.resetState();
  }

  /** Shallow patch helper for immutable state updates. */
  private patchData(patch: Partial<CrmProposalHistoryWidgetData>): void {
    const current = this._data$.value;
    const patchKeys = Object.keys(patch) as (keyof CrmProposalHistoryWidgetData)[];
    const hasChanges = patchKeys.some(key => current[key] !== patch[key]);
    if (!hasChanges) {
      return;
    }
    this._data$.next({ ...current, ...patch });
  }

  /** Builds default first page for table/list loading. */
  private createDefaultPage(): Page {
    const page = new Page();
    page.pageNumber = 0;
    page.size = this.resolvePageSizeForMode();
    return page;
  }

  /** Clones caller page into a safe mutable `Page` instance owned by this store. */
  private clonePage(page?: Page | null): Page {
    const source = page || this.createDefaultPage();
    const cloned = new Page();
    cloned.pageNumber = source.pageNumber || 0;
    cloned.size = source.size || this.resolvePageSizeForMode();
    cloned.filter = source.filter;
    cloned.orders = source.orders;
    cloned.searchValue = source.searchValue;
    cloned.searchOptionValue = source.searchOptionValue;
    return cloned;
  }

  private normalizePageForCurrentMode(page?: Page | null): Page {
    const normalizedPage = this.clonePage(page);
    normalizedPage.size = this.resolvePageSizeForMode();
    return normalizedPage;
  }

  /**
   * @summary Set active query identity for summary + list.
   * @description
   * Called when contact/account context changes. If query changes, store resets
   * previous dataset and starts fresh init for the new identity.
   *
   * @param queryKey Account id or contact email.
   * @businessRule Empty query must hard-clear state to avoid stale data leakage.
   * @testHint Switching between two different query keys must never show mixed totals.
   */
  async setQuery(queryKey: string): Promise<void> {
    const normalizedKey = (queryKey || '').trim();
    if (!normalizedKey) {
      this.clear();
      return;
    }

    if (this.data.queryKey === normalizedKey) {
      if (this.loadInFlight) {
        await this.loadInFlight;
        return;
      }
      if (!this.data.pageData) {
        await this.init();
      }
      return;
    }

    this.patchData({
      queryKey: normalizedKey,
      summaryQueryKey: '',
      currentPage: this.createDefaultPage(),
      pageData: null,
      total: 0,
      stageStats: [],
      stageList: [],
      gridMounting: false,
      gridMounted: false,
      gridLoadError: false,
    });
    await this.init();
  }

  /**
   * @summary Initial load entry for current query.
   * @description
   * Loads both summary + first list page for the current query context.
   * Delegates to {@link setQuery} when caller passes a different query.
   */
  async init(queryKey?: string): Promise<void> {
    if (queryKey != null && queryKey.trim() !== this.data.queryKey) {
      await this.setQuery(queryKey);
      return;
    }

    if (!this.data.queryKey) {
      return;
    }

    const page = this.clonePage(this.data.currentPage);
    await this.runLoad(page, true, true);
  }

  /**
   * @summary Refresh summary block and reset list to first page.
   * @businessRule User-triggered summary refresh must use the current query only.
   */
  async refreshSummary(): Promise<void> {
    if (!this.data.queryKey) {
      return;
    }

    const page = this.createDefaultPage();
    this.patchData({ currentPage: page });
    await this.runLoad(page, true, true);
  }

  /**
   * @summary Apply page/filter/sort transition for list data.
   * @param page Target paging payload from grid component.
   * @testHint Verify page size is normalized by load-all mode before API call.
   */
  async changePage(page: Page): Promise<void> {
    if (!this.data.queryKey) {
      return;
    }

    const targetPage = this.normalizePageForCurrentMode(page);
    this.patchData({ currentPage: targetPage });
    await this.runLoad(targetPage, false, false);
  }

  /**
   * @summary Toggle load-all behavior for subsequent list requests.
   * @description Updates store page size strategy only; fetch is driven by grid caller.
   */
  setLoadAll(loadAll: boolean): void {
    const nextLoadAll = !!loadAll;
    if (this.data.loadAll === nextLoadAll) {
      return;
    }
    const nextPage = this.clonePage(this.data.currentPage);
    nextPage.size = nextLoadAll ? -1 : this.pageSize;
    this.patchData({
      loadAll: nextLoadAll,
      currentPage: nextPage,
    });
  }

  /**
   * @summary Mark modal-grid mount as started.
   * @description Used by shell to surface "body loading" before first grid paint.
   */
  beginGridMount(): void {
    this.setGridMountState(true, false, false);
  }

  /**
   * @summary Mark modal-grid mount as completed successfully.
   */
  completeGridMount(): void {
    this.setGridMountState(false, true, false);
  }

  /**
   * @summary Mark modal-grid mount as failed.
   * @businessRule Error state is shown only when grid is not mounting/loading.
   */
  failGridMount(): void {
    this.setGridMountState(false, false, true);
  }

  /**
   * @summary Clear transient grid mount flags.
   * @description Keeps query/list data but resets shell mount state.
   */
  resetGridMountState(): void {
    this.setGridMountState(false, false, false);
  }

  private setGridMountState(gridMounting: boolean, gridMounted: boolean, gridLoadError: boolean): void {
    this.patchData({
      gridMounting,
      gridMounted,
      gridLoadError,
    });
  }

  /**
   * ### Stage Filter Builder
   * Produces stage dropdown options using stage names only and best available source:
   * 1) summary stats (preferred), 2) current list rows (fallback), 3) raw stage list.
   */
  private buildStageFilterOptions(
    stageList: any[],
    stageStats: InquiryStageStat[],
    listRows: any[],
  ): ProposalHistoryStageFilterOption[] {
    const totalsByStageId = new Map<number, number>();
    (stageStats || []).forEach(stat => {
      const stageId = Number(stat?.stageId);
      if (!Number.isFinite(stageId)) {
        return;
      }
      totalsByStageId.set(stageId, Number(stat?.total || 0));
    });

    // Truth source pattern: map from stageList first, then optionally prune by what exists.
    const mapBaseOption = (stage: any): ProposalHistoryStageFilterOption => {
      const stageId = Number(stage?.automateDataStateId || 0);
      const name = stage?.dataStateName || 'Unknown';
      const text = name;
      if (stageId === 1) {
        return { text, value: `[${stageId}, null]` };
      }
      return { text, value: stageId };
    };

    if (!stageList?.length) {
      return [];
    }

    // Preferred source for counts + existence: summary stats.
    if (totalsByStageId.size > 0) {
      const filtered = stageList
        .filter(stage => (totalsByStageId.get(Number(stage?.automateDataStateId)) || 0) > 0)
        .map(stage => mapBaseOption(stage));
      if (filtered.length > 0) {
        return filtered;
      }
      // If stats shape mismatches ids, keep base list instead of emptying the dropdown.
      return stageList.map(stage => mapBaseOption(stage));
    }

    // Fallback: no stats yet -> prune by ids that exist in current list rows.
    const rowCountsByStageId = new Map<number, number>();
    (listRows || []).forEach(row => {
      const stageId = Number(row?.automateDataStateId);
      if (!Number.isFinite(stageId)) {
        return;
      }
      rowCountsByStageId.set(stageId, (rowCountsByStageId.get(stageId) || 0) + 1);
    });

    if (rowCountsByStageId.size > 0) {
      const filtered = stageList
        .filter(stage => rowCountsByStageId.has(Number(stage?.automateDataStateId)))
        .map(stage => mapBaseOption(stage));
      if (filtered.length > 0) {
        return filtered;
      }
    }

    return stageList.map(stage => mapBaseOption(stage));
  }

  /**
   * @summary Execute core parallel load pipeline for list + stage metadata.
   * @description
   * Uses request sequence guard to ensure only latest response updates state.
   * This prevents race conditions when users change filters/pages quickly.
   *
   * @param page Normalized request page.
   * @param loadSummary Whether summary counters should be recomputed.
   * @param forceReloadStages Whether stage catalog should be reloaded now.
   * @testHint Simulate two rapid requests; only newest response should persist.
   */
  private async runLoad(page: Page, loadSummary: boolean, forceReloadStages: boolean): Promise<void> {
    const loadTask = (async () => {
      const requestId = ++this.requestSeq;
      const normalizedPage = this.normalizePageForCurrentMode(page);
      this.patchData({
        currentPage: normalizedPage,
        ...(loadSummary ? { summaryLoading: true, listLoading: true } : { listLoading: true }),
      });

      const shouldLoadStages = forceReloadStages || !this.data.stageList?.length;
      try {
        const [historyResp, stageResp] = await Promise.all([
          this.proposalHistoryRepository.getProposalHistoryPaged(this.data.queryKey, normalizedPage).toPromise(),
          shouldLoadStages
            ? this.proposalHistoryRepository.getOpportunityStages().toPromise()
            : Promise.resolve(this.createStageFallbackResponse()),
        ]);

        if (requestId !== this.requestSeq) {
          return;
        }

        const mapped = mapProposalHistoryParallelLoad(historyResp, stageResp, normalizedPage);
        const nextPatch: Partial<CrmProposalHistoryWidgetData> = {
          stageList: mapped.stageList,
          pageData: mapped.pageData,
        };
        if (loadSummary || this.data.summaryQueryKey !== this.data.queryKey) {
          nextPatch.total = mapped.total;
          nextPatch.stageStats = mapped.stageStats;
          nextPatch.summaryQueryKey = this.data.queryKey;
        }
        nextPatch.gridLoadError = false;
        this.patchData(nextPatch);
      } catch (error) {
        if (requestId === this.requestSeq) {
          this.patchData({ gridLoadError: true });
        }
        throw error;
      } finally {
        if (requestId === this.requestSeq) {
          this.patchData({ summaryLoading: false, listLoading: false });
        }
      }
    })();

    this.loadInFlight = loadTask;
    try {
      await loadTask;
    } finally {
      if (this.loadInFlight === loadTask) {
        this.loadInFlight = null;
      }
    }
  }

  /** Hard resets store to initial empty snapshot and clears request guards. */
  private resetState(): void {
    this.requestSeq = 0;
    this.loadInFlight = null;
    this._data$.next(createEmptyCrmProposalHistoryWidgetData());
  }

  /** Public clear command used by consumers when query is empty/unavailable. */
  clear(): void {
    this.resetState();
  }

  private resolvePageSizeForMode(): number {
    return this.data.loadAll ? -1 : this.pageSize;
  }

  private createStageFallbackResponse(): ReturnResult<AutomateDataState[]> {
    return {
      message: '',
      result: this.data.stageList,
    };
  }

  /**
   * ### Lazy Table Component Loader
   * Preloads and caches the grid component type so host components can mount it
   * dynamically without re-importing chunk multiple times.
   */
  async loadTableComponent(): Promise<void> {
    if (this.data.lazyTableComponent) {
      return;
    }

    this.patchData({ loadingLazyComponent: true });
    try {
      const component = await loadProposalHistoryGridComponent();
      this.patchData({ lazyTableComponent: component });
    } finally {
      this.patchData({ loadingLazyComponent: false });
    }
  }
}
